import { urlFor } from '@/sanity/lib/image';
import { getImageDimensions } from '@sanity/asset-utils';
// Fallback: loosen the image source type to avoid missing type exports from @sanity/image-url
type SanityImageSource = any;
import { DEFAULT_IMAGE_QUALITY, DEFAULT_IMAGE_WIDTH, VIDEO_VARIANTS } from '../constants';
import { FinalValues, VideoImageData } from '../interfaces';
export const getDirectImageUrl = (_image: any): string | undefined => undefined;

export const getFinalValues = (variant: VideoImageData['variant'], block: VideoImageData): FinalValues => {
  const defaultRes = {
    thumbnail: block.thumbnail,
    heading: block.imageHeading ?? '',
    description: block.description,
    buttons: null,
  };
  switch (variant) {
    case VIDEO_VARIANTS.ImageVideoWithText:
      return {
        thumbnail: block.image,
        heading: block.imageHeading ?? '',
        description: block.description,
        buttons: block.buttons ?? null,
        isVideo: block.isVideo,
      };
    case VIDEO_VARIANTS.VideoWithText:
      return {
        thumbnail: block.thumbnail,
        heading: block.textOverlay?.heading ?? '',
        description: block.textOverlay?.description ?? '',
        buttons: null,
      };
    case VIDEO_VARIANTS.VideoWithoutText:
    default:
      return defaultRes;
  }
};

// Vimeo ID extractor
export const getVimeoVideoId = (url: string): string => {
  if (!url) return '';
  if (/^\d+$/.test(url)) return url;
  const match = url.match(/(?:vimeo\.com\/.*?\/)?(\d+)/);
  return match ? match[1] : url;
};

// Duration formatter
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

type ImageBuilder = ReturnType<typeof urlFor>;

export const getImageBuilder = (
  src: SanityImageSource,
  width = DEFAULT_IMAGE_WIDTH,
  quality = DEFAULT_IMAGE_QUALITY,
): ImageBuilder => {
  if (typeof src === 'string') return urlFor(src).auto('format');
  // Check if it's has ref
  if ('asset' in src && src.asset?._ref) {
    const imageRef = src?.asset?._ref;
    const { width, height } = getImageDimensions(imageRef);
    return urlFor(src).width(width).height(height).auto('format');
  }

  // If it has a URL property
  if ('url' in src && src.url) return urlFor(src.url).width(width).fit('crop').quality(quality).auto('format');

  return urlFor(src).auto('format');
};

// Check if the given object is a valid image
export const isValidImage = (image: string | Record<string, Record<string, string> | string>): boolean => {
  return !!(typeof image === 'string' || image?.url || image?.asset);
};
